import { useScreenSize } from '@/themes/media-query'
import { Button, DataGrid, Popup } from 'devextreme-react'
import { Accordion, Item as AccordionItem } from 'devextreme-react/accordion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLoaderData } from 'react-router-typesafe'
import {
  Button as GridButton,
  Column,
  ColumnChooser,
  DataGridRef,
  DataGridTypes,
  Editing,
  Export,
  FilterPanel,
  FilterRow,
  HeaderFilter,
  Item,
  Pager,
  Scrolling,
  Search,
  SearchPanel,
  Selection,
  Sorting,
  StateStoring,
  Summary,
  Toolbar,
  TotalItem,
} from 'devextreme-react/data-grid'
import dxDataGrid, { ColumnButtonClickEvent, ExportingEvent, RowDblClickEvent } from 'devextreme/ui/data_grid'
import ODataStore from 'devextreme/data/odata/store'
import notify from 'devextreme/ui/notify'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { attivita_lavorativa } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { confirm } from 'devextreme/ui/dialog'
import { Workbook } from 'exceljs'
import { exportDataGrid } from 'devextreme/excel_exporter'
import saveAs from 'file-saver'
import { ClickEvent } from 'devextreme/ui/button'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { ContentReadyEvent } from 'devextreme/ui/accordion'
import {
  createDateFilterItemDescriptor,
  FormFilterItemDescriptorType,
} from '@/components/filter-form/GenericFilterForm.types'
import GenericFilterForm from '@/components/filter-form/GenericFilterForm'
import { hoursPageRouteLoader } from '@/routes/hours/HoursPage.route'
import { AxiosError, AxiosResponse } from 'axios'
import { getApiErrorMessage } from '@/auth/api/apiErrorMessage'
import { computeTempo } from '@/routes/hours/utils'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import DateColumn from '@/components/date-column/DateColumn'
import {
  columnSourceFactory,
  isUserRoleAllowed,
  onDataGridSelectedRowButtonClick,
  calculateDateRangeFilterValue,
} from '@/routes/utils'
import { Roles } from '@/auth/azure/Roles'
import { PopupRef } from 'devextreme-react/cjs/popup'
import MonthUserCheckSelector from '@/routes/hours/MonthUserCheckSelector'
import { gridTotalItemsLabel } from '@/constants'
import DocumentHead from '@/components/document-head/DocumentHead'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'
import { useUiStore } from '@/store/uiStore'
import InsertHolidaysSelector from '@/routes/hours/InsertHolidaysSelector'

const HoursPage = () => {
  const { title, aziende, impiegati, userInfo, defaultCRUDAllowedRoles } = useLoaderData<typeof hoursPageRouteLoader>()
  const impiegatoDefault = impiegati.data.value.find((impiegato) => impiegato.fullname === userInfo?.name)
  const token = useTokenRefresh()

  const popupInsertHolidaysSelectorRef = useRef<PopupRef>(null)

  const popupCurrentMonthUserCheckRef = useRef<PopupRef>(null)
  const [currentMonthUserCheckData, setCurrentMonthUserCheckData] = useState<{
    monthList: { index: number; description: string }[]
    formData: {
      month: { index: number; description: string }
    }
    onSubmitEditor: (
      e: any,
      params: {
        month: number | undefined
      },
    ) => Promise<void>
  }>()
  const currentScreenSize = useScreenSize()
  const gridRef = useRef<DataGridRef>(null)
  const gridRefSpese = useRef<DataGridRef>(null)
  const navigate = useNavigate()
  const { service, client } = useQsAdminApiManager()

  const now: Date = new Date()

  const getFiltersConfiguration = useCallback((): FormFilterItemDescriptorType[] => {
    const fltConf: FormFilterItemDescriptorType[] = [
      {
        fieldName: 'azienda',
        placeHolder: 'Azienda',
        valueKeyName: 'id',
        valueDisplayExpr: 'nome',
        values: aziende.data.value,
        composeFilterItem(): any[] | null {
          if (this.currentValue === undefined || this.currentValue === null) return null
          return [['sede/azienda/id', this.currentValue]]
        },
      },
      {
        fieldName: 'impiegato',
        placeHolder: 'Impiegato',
        valueKeyName: 'id',
        valueDisplayExpr: 'fullname',
        defaultValue: impiegatoDefault?.id,
        values: impiegati.data.value,
        composeFilterItem(): any[] | null {
          if (this.currentValue === undefined || this.currentValue === null) return null
          return [['impiegato/id', this.currentValue]]
        },
      },
      createDateFilterItemDescriptor('data', 'Data', 'Mese corrente'),
    ]
    console.log('GENFLT - GETCONF :', fltConf)
    return fltConf
  }, [aziende, impiegati])

  const { hoursView, setHoursView } = useUiStore()
  const initialFilterConfiguration = useMemo(() => getFiltersConfiguration(), [getFiltersConfiguration])
  const [filterConfiguration, setFilterConfiguration] = useState(initialFilterConfiguration)
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false)
  const [currentPeriodoFilter, setCurrentPeriodoFilter] = useState<unknown>()
  const [currentImpiegatoFilter, setCurrentImpiegatoFilter] = useState<unknown>()
  const [currentAziendaFilter, setCurrentAziendaFilter] = useState<unknown>()
  const [currentFilter, setCurrentFilter] = useState<any[]>([])
  const [totals, setTotals] = useState<{
    extraTime: number
    totalMealVoucher: number
    totalTime: { minutes: number; hours: number }
    transferTime: number
  }>()

  const getGridHeight = useCallback(() => {
    let h = '72vh'
    if (currentScreenSize.isMedium) {
      h = '68vh'
    } else if (currentScreenSize.isSmall) {
      h = '120vh'
    } else if (currentScreenSize.isXSmall) {
      h = '65vh'
    }
    return h
  }, [currentScreenSize])

  const getCurrentFilter = useCallback((): any[] => {
    let applied: boolean = false
    const currentFlts = []
    for (const flt of filterConfiguration) {
      const fltValue = flt.composeFilterItem()
      if (fltValue !== null) {
        currentFlts.push(fltValue)
      }
      if (!applied && flt.currentValue !== flt.defaultValue) applied = true
      if (flt.fieldName === 'data') setCurrentPeriodoFilter(flt.currentValue)
      if (flt.fieldName === 'impiegato') setCurrentImpiegatoFilter(flt.currentValue)
      if (flt.fieldName === 'azienda') setCurrentAziendaFilter(flt.currentValue)
    }
    setFiltersApplied(applied)
    console.log('GENFLT - CURRENT:', currentFlts)
    return currentFlts
  }, [filterConfiguration])

  const handleCurrentMonthSummaryClick = (e: ClickEvent) => {
    gridRef?.current?.instance().beginCustomLoading('Esportazione totale mese corrente in corso....')
    client
      .get('/api/attivitalavorativa/currentmonthsummary', {
        headers: {
          'Content-Disposition': `attachment;filename=HS-${now.getFullYear()}-${now.getMonth() + 1}.zip`,
          'Content-Type': 'application/octet-stream',
        },
        responseType: 'arraybuffer',
      })
      .then((response: AxiosResponse) => {
        saveAs(
          new Blob([response.data], { type: 'application/octet-stream' }),
          `HS-${now.getFullYear()}-${now.getMonth() + 1}.zip`,
        )
      })
      .catch((error: AxiosError) => {
        notify(`Errore esportazione totale mese corrente. Dettagli : ${error}`, 'error', 2000)
      })
      .finally(() => {
        gridRef?.current?.instance().endCustomLoading()
      })
  }

  const handleCurrentMonthtRiprefundClick = (e: ClickEvent) => {
    gridRef?.current?.instance().beginCustomLoading('Esportazione rimborsi in corso....')
    client
      .get('/api/attivitalavorativa/currentmonthtriprefund', {
        headers: {
          'Content-Disposition': `attachment;filename=HTF-${now.getFullYear()}-${now.getMonth() + 1}.xlsx`,
          'Content-Type': 'application/octet-stream',
        },
        responseType: 'arraybuffer',
      })
      .then((response: AxiosResponse) => {
        saveAs(
          new Blob([response.data], { type: 'application/octet-stream' }),
          `HTF-${now.getFullYear()}-${now.getMonth() + 1}.xlsx`,
        )
      })
      .catch((error: AxiosError) => {
        notify(`Errore esportazione rimborsi. Dettagli : ${error}`, 'error', 2000)
      })
      .finally(() => {
        gridRef?.current?.instance().endCustomLoading()
      })
  }

  const handleCurrentMonthUserCheckClick = (e: ClickEvent) => {
    const formatter = new Intl.DateTimeFormat('it', {
      month: 'long',
    })
    setCurrentMonthUserCheckData({
      monthList: [
        {
          index: now.getMonth() === 0 ? 12 : now.getMonth(),
          description: formatter.format(new Date(now.getFullYear(), now.getMonth() - 1)),
        },
        { index: now.getMonth() + 1, description: formatter.format(new Date(now.getFullYear(), now.getMonth())) },
      ],
      formData: {
        month: {
          index: now.getMonth() + 1,
          description: formatter.format(new Date(now.getFullYear(), now.getMonth())),
        },
      },
      onSubmitEditor: async (
        e: any,
        params: {
          month: number | undefined
        },
      ) => {
        e.preventDefault()
        try {
          const response = await client.get(`/api/attivitalavorativa/monthusercheck?month=${params.month}`, {
            headers: {
              'Content-Disposition': `attachment;filename=HS-${now.getFullYear()}-${params.month}-${impiegatoDefault?.cognome?.toUpperCase()}.zip`,
              'Content-Type': 'application/octet-stream',
            },
            responseType: 'arraybuffer',
          })
          console.log('DATARE2', now, now.getFullYear(), now.getMonth())
          saveAs(
            new Blob([response.data], { type: 'application/octet-stream' }),
            `HS-${now.getFullYear()}-${params.month}-${impiegatoDefault?.fullname?.toUpperCase()}.zip`,
          )
        } catch (error: unknown) {
          notify(
            `Errore esportazione check utente mese. Dettagli : ${getApiErrorMessage(error, 'errore imprevisto.')}`,
            'error',
            6000,
          )
        } finally {
          popupCurrentMonthUserCheckRef.current?.instance().hide()
        }
      },
    })

    popupCurrentMonthUserCheckRef.current?.instance().show()
  }
  const duplicateActivity = (hour: attivita_lavorativa) => {
    gridRef?.current?.instance().beginCustomLoading('Duplicazione attività in corso....')
    console.log('attivita', hour)
    const activityDate = new Date()
    activityDate.setHours(13)
    activityDate.setMinutes(0)
    activityDate.setSeconds(0)
    activityDate.setMilliseconds(0)
    client
      .post(
        '/api/attivitalavorativa/duplicateactivity',
        { activityId: hour.id, activityDate: activityDate },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .then((response: AxiosResponse) => {
        gridRef.current?.instance().refresh()
      })
      .catch((error: AxiosError) => {
        notify(`Errore duplicazione attività. Dettagli : ${error}`, 'error', 2000)
      })
      .finally(() => {
        gridRef.current?.instance().endCustomLoading()
      })
  }

  const handleDuplicateActivityClick = (hour?: attivita_lavorativa) => {
    if (hour) {
      duplicateActivity(hour)
    } else {
      onDataGridSelectedRowButtonClick(gridRef, (rowdata: attivita_lavorativa) => {
        duplicateActivity(rowdata)
      })
    }
  }

  const deleteActivity = (hour: attivita_lavorativa) => {
    const result = confirm(
      `<i>Vuoi davvero eliminare l'attività lavorativa <b>${hour.ded_Dis}</b>?</i>`,
      'Eliminazione attività lavorativa',
    )
    result.then((dialogResult) => {
      if (dialogResult === false) return
      service
        .attivita_lavorativa(hour.id)
        .delete()
        .then(() => {
          // Aggiornamento griglia
          gridRef.current?.instance().refresh()

          notify(
            {
              message: `Attività lavorativa '${hour['id']}' eliminata con successo`,
              type: 'success',
              displayTime: 5000,
            },
            {
              position: 'bottom center',
              direction: 'up-push',
            },
          )
        })
        .catch((error: unknown) => {
          console.log('error', error)
          notify(
            {
              message: `Errore eliminazione attività lavorativa: ${error}`,
              type: 'error',
              displayTime: 5000,
            },
            {
              position: 'bottom center',
              direction: 'up-push',
            },
          )
        })
    })
  }
  const onDeleteClick = (hour?: attivita_lavorativa) => {
    if (hour) {
      deleteActivity(hour)
    } else {
      onDataGridSelectedRowButtonClick(gridRef, (rowdata: attivita_lavorativa) => {
        deleteActivity(rowdata)
      })
    }
  }

  const onExporting = (e: ExportingEvent) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet(e.element.id === 'hours-datagrid' ? 'Hours' : 'Expenses')
    const component: dxDataGrid = e.component
    exportDataGrid({ component, worksheet }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], { type: 'application/octet-stream' }),
          e.element.id === 'hours-datagrid' ? 'Hours.xlsx' : 'Expenses.xlsx',
        )
      })
    })
  }

  const computeTempoUfficioValue = (rowData: attivita_lavorativa) => {
    return computeTempo(rowData.tempo_ufficio)
  }

  const computeTempoClienteValue = (rowData: attivita_lavorativa) => {
    return computeTempo(rowData.tempo_cliente)
  }

  const computeTempoTrasfertaValue = (rowData: attivita_lavorativa) => {
    return computeTempo(rowData.tempo_trasferta)
  }

  const calculateTotals = (options: DataGridTypes.CustomSummaryInfo) => {
    if (options.summaryProcess !== 'finalize') return
    options.totalValue = `Totale [HH.MM] ${totals?.totalTime} - Straordinari ${totals?.extraTime} - Trasferta ${totals?.transferTime} - Buoni pasto [N] ${totals?.totalMealVoucher}`
  }

  const hoursDataSource = {
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/attivita_lavorativa`,
      key: 'id',
      keyType: 'Int32',
      version: 4,
      errorHandler: (e) => {
        console.error(e.errorDetails)
        if (!e.errorDetails) return
        notify(
          {
            message: `Errore : ${e.errorDetails?.message}`,
            type: 'error',
            displayTime: 5000,
          },
          {
            position: 'bottom center',
            direction: 'up-push',
          },
        )
      },
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    expand: [
      'impiegato($expand=reparto)',
      'centro_costo',
      'piattaforma',
      'causale',
      'task',
      'intervento',
      'sede($expand=azienda($expand=agente($expand=commerciale_qs)))',
    ],
    filter: currentFilter?.length > 0 ? currentFilter : null,
    requireTotalCount: true,
  }

  useEffect(() => {
    if (currentPeriodoFilter && currentImpiegatoFilter) {
      const { minDate, maxDate } = calculateDateRangeFilterValue(currentPeriodoFilter)
      client
        .get(
          `/api/attivitalavorativa/computedaterangetotals?firstRangeDate=${minDate.toISOString()}&lastRangeDate=${maxDate.toISOString()}&userId=${currentImpiegatoFilter ?? ''}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .then(
          (
            response: AxiosResponse<
              {
                extraTime: number
                totalMealVoucher: number
                totalTime: { minutes: number; hours: number }
                transferTime: number
              },
              {
                extraTime: number
                totalMealVoucher: number
                totalTime: { minutes: number; hours: number }
                transferTime: number
              }
            >,
          ) => {
            setTotals(response.data)
          },
        )
        .catch((error: AxiosError) => {
          notify(`Errore calcolo totali ore. Dettagli : ${error}`, 'error', 2000)
          setTotals({ extraTime: 0, totalMealVoucher: 0, totalTime: { minutes: 0, hours: 0 }, transferTime: 0 })
        })
    } else setTotals({ extraTime: 0, totalMealVoucher: 0, totalTime: { minutes: 0, hours: 0 }, transferTime: 0 })
  }, [currentPeriodoFilter, currentImpiegatoFilter])

  useEffect(() => {
    getCurrentFilter()
  }, [])

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'end', alignItems: 'center', marginRight: '2em' }}>
        <Button
          text="Prova la nuova dashboard"
          icon="warning"
          type="success"
          stylingMode="outlined"
          onClick={() => {
            setHoursView(hoursView === 'dashboard' ? 'grid' : 'dashboard')
            navigate('/workactivities/hours/dashboard')
          }}
        />
      </div>

      <div className="accordion-generic-filter-form">
        <Accordion
          className={filtersApplied ? 'with-filter' : ''}
          collapsible={true}
          onContentReady={(e: ContentReadyEvent<any, any>) => {
            e.component?.collapseItem(0)
          }}
        >
          <AccordionItem icon="filter" title={'Filtri'}>
            <GenericFilterForm
              FormItemDescriptors={filterConfiguration}
              resetFilterClicked={function (e: ClickEvent): void {
                setFilterConfiguration(getFiltersConfiguration())
              }}
              filterValueChanged={function (): void {
                const currentFlts = getCurrentFilter()
                console.log('currentFlts', currentFlts)
                console.log('GENFLT - VALUE CHNG', currentFlts)
                if (JSON.stringify(currentFlts) !== JSON.stringify(currentFilter)) {
                  console.log('GENFLT - SETTING FILT', currentFlts)
                  setCurrentFilter(currentFlts)
                }
              }}
            />
          </AccordionItem>
        </Accordion>
      </div>
      <DataGrid
        id={'hours-datagrid'}
        height={getGridHeight()}
        dataSource={hoursDataSource}
        noDataText="Nessuna attività lavorativa trovata"
        className={'dx-card wide-card'}
        showBorders={false}
        showColumnLines={true}
        wordWrapEnabled={false}
        showRowLines={true}
        focusedRowEnabled={true}
        rowAlternationEnabled={true}
        allowColumnResizing={true}
        allowColumnReordering={true}
        width="100%"
        onExporting={onExporting}
        ref={gridRef}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
        remoteOperations={{
          filtering: true,
          grouping: true,
          groupPaging: true,
          paging: true,
          sorting: true,
          summary: false,
        }}
        grouping={{
          // Fondamentale per il raggruppamento veloce!!!
          autoExpandAll: true,
        }}
        groupPanel={{
          visible: currentScreenSize.isLarge || currentScreenSize.isMedium,
          emptyPanelText: 'Trascina qui una colonna per raggruppare',
        }}
        onRowDblClick={(e: RowDblClickEvent<any, any>) => {
          if (e.rowType === 'data') navigate(`${e.data.id}`)
        }}
        onContextMenuPreparing={(e: DataGridTypes.ContextMenuPreparingEvent<any, any>) => {
          if (e.row?.rowType === 'data') {
            if (!e.items) e.items = []
            e.items.push(
              {
                text: 'Edita',
                icon: 'edit',
                visible: true,
                onItemClick: () => {
                  navigate(`${e.row?.data.id}`)
                },
              },
              {
                text: 'Duplica',
                icon: 'copy',
                visible: true,
                onItemClick: () => {
                  handleDuplicateActivityClick(e.row?.data)
                },
              },
              {
                text: 'Elimina',
                icon: 'trash',
                visible: true,
                onItemClick: () => {
                  onDeleteClick(e.row?.data)
                },
              },
            )
          }
        }}
      >
        <Pager
          visible={true}
          allowedPageSizes={'auto'}
          displayMode={'compact'}
          showPageSizeSelector={false}
          showInfo={true}
          showNavigationButtons={false}
          infoText={gridTotalItemsLabel}
        />
        <FilterRow visible={true} />
        <SearchPanel visible={true} width={currentScreenSize.isXSmall ? 200 : 250} />
        <Sorting mode="multiple"></Sorting>
        <FilterPanel visible={true} />
        <Export enabled={true} />
        <Editing allowAdding={true} />
        <HeaderFilter visible={true} />
        <Selection mode="single" />
        <ColumnChooser enabled={true} />
        <Scrolling mode="virtual" />
        <StateStoring enabled={true} type="localStorage" storageKey={'hours-datagrid'} savingTimeout={50} />
        <Column
          visible={false}
          type="buttons"
          width={currentScreenSize.isXSmall || currentScreenSize.isSmall ? '10%' : '2%'}
          alignment="left"
          showInColumnChooser={false}
        >
          <GridButton
            hint="Details"
            icon="search"
            onClick={(e: ColumnButtonClickEvent) => {
              navigate(`${e.row?.data.id}`)
            }}
          ></GridButton>
        </Column>
        <Column
          dataField={'id'}
          caption="ID"
          visible={false}
          dataType="number"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 0 : undefined
          }
        >
          <HeaderFilter dataSource={columnSourceFactory(token, 'attivita_lavorativa', 'id', true)}></HeaderFilter>
        </Column>
        <Column
          dataField={'impiegato.fullname'}
          caption="IMPIEGATO"
          visible={false}
          dataType="string"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined
          }
        >
          <HeaderFilter dataSource={columnSourceFactory(token, 'user', 'fullname', true)}></HeaderFilter>
        </Column>
        {DateColumn({
          dataField: 'data',
          caption: 'DATA',
          width: 100,
          format: 'dd/MM/yyyy',
          defaultSortOrder: 'desc',
          hidingPriority:
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 16 : undefined,
        })}
        <Column
          dataField={'centro_costo.nome'}
          caption="CENTRO DI COSTO"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 15 : undefined
          }
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
        >
          <HeaderFilter dataSource={columnSourceFactory(token, 'centro_costo', 'nome')}>
            <Search enabled={true} searchExpr={'nome'} />
          </HeaderFilter>
        </Column>
        <Column
          dataField={'piattaforma.nome'}
          caption="PIATTAFORMA"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 14 : undefined
          }
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
        >
          <HeaderFilter dataSource={columnSourceFactory(token, 'piattaforma_attivita_lavorativa', 'nome')}>
            <Search enabled={true} searchExpr={'nome'} />
          </HeaderFilter>
        </Column>
        <Column
          dataField={'task.ded_Dis'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: attivita_lavorativa = cellData.row.data
            return (
              <ObjectCodeLink
                url={`/tasks/${data.task?.id}`}
                text={data.task?.ded_Dis}
                disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
              ></ObjectCodeLink>
            )
          }}
          caption={'TASK'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 12 : undefined
          }
        >
          <HeaderFilter dataSource={columnSourceFactory(token, 'task', 'ded_Dis', true)}>
            <Search enabled={true} searchExpr={'ded_Dis'} />
          </HeaderFilter>
        </Column>
        <Column
          dataField={'intervento.ded_Dis'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: attivita_lavorativa = cellData.row.data
            return (
              <ObjectCodeLink
                url={`/interventions/${data.intervento?.id}`}
                text={data.intervento?.ded_Dis}
                disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
              ></ObjectCodeLink>
            )
          }}
          caption={'INTERVENTO'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 11 : undefined
          }
        >
          <HeaderFilter dataSource={columnSourceFactory(token, 'intervento', 'ded_Dis', true)}>
            <Search enabled={true} searchExpr={'ded_Dis'} />
          </HeaderFilter>
        </Column>
        <Column
          dataField={'sede.azienda.nome'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: attivita_lavorativa = cellData.row.data
            return (
              <ObjectCodeLink
                url={`/clients/${data.sede?.azienda?.id}`}
                text={data.sede?.azienda?.nome}
                disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
              ></ObjectCodeLink>
            )
          }}
          caption="AZIENDA"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 13 : undefined
          }
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
        >
          <HeaderFilter dataSource={columnSourceFactory(token, 'azienda', 'nome')}>
            <Search enabled={true} searchExpr={'nome'} />
          </HeaderFilter>
        </Column>
        <Column
          dataField={'sede.nome'}
          caption="SEDE"
          allowHeaderFiltering={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 2 : undefined
          }
        />
        <Column
          name={'tempo_ufficio'}
          caption="UFFICIO"
          calculateCellValue={computeTempoUfficioValue}
          dataType="datetime"
          format={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          allowHeaderFiltering={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 10 : undefined
          }
        />
        <Column
          name={'tempo_cliente'}
          caption="CLIENTE"
          calculateCellValue={computeTempoClienteValue}
          dataType="datetime"
          format={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          allowHeaderFiltering={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 9 : undefined
          }
        />
        <Column
          name={'tempo_trasferta'}
          caption="TRASFERTA"
          calculateCellValue={computeTempoTrasfertaValue}
          dataType="datetime"
          format={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          allowHeaderFiltering={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 8 : undefined
          }
        />
        <Column
          dataField={'spese_trasferta'}
          caption="SPESE TRASFERTA"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 7 : undefined
          }
        >
          <HeaderFilter
            dataSource={columnSourceFactory(token, 'attivita_lavorativa', 'spese_trasferta', true)}
          ></HeaderFilter>
        </Column>
        <Column
          dataField={'spese_vitto'}
          caption="VITTO"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 6 : undefined
          }
        >
          <HeaderFilter
            dataSource={columnSourceFactory(token, 'attivita_lavorativa', 'spese_vitto', true)}
          ></HeaderFilter>
        </Column>
        <Column
          dataField={'spese_alloggio'}
          caption="ALLOGGIO"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 5 : undefined
          }
        >
          <HeaderFilter
            dataSource={columnSourceFactory(token, 'attivita_lavorativa', 'spese_alloggio', true)}
          ></HeaderFilter>
        </Column>
        <Column
          dataField={'note'}
          caption="NOTE"
          allowHeaderFiltering={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
          }
        />
        <Column
          dataField={'causale.nome'}
          caption="CAUSALE"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 3 : undefined
          }
        >
          <HeaderFilter dataSource={columnSourceFactory(token, 'causale_centro_costo', 'nome')}>
            <Search enabled={true} searchExpr={'nome'} />
          </HeaderFilter>
        </Column>
        {currentImpiegatoFilter !== undefined && currentImpiegatoFilter !== null && (
          <Summary calculateCustomSummary={calculateTotals}>
            <TotalItem column="spese_trasferta" summaryType="sum" displayFormat="€ {0}" />
            <TotalItem column="spese_vitto" summaryType="sum" displayFormat="€ {0}" />
            <TotalItem column="spese_alloggio" summaryType="sum" displayFormat="€ {0}" />
          </Summary>
        )}
        <Toolbar>
          <Item
            location="before"
            locateInMenu="never"
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Refresh',
              text: 'Refresh',
              icon: 'refresh',
              onClick: (e: ClickEvent) => {
                gridRef.current?.instance().refresh()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item name="groupPanel" />
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            name="addRowButton"
            options={{
              onClick: () => {
                navigate(`new`)
              },
              hint: 'Nuova attività',
              text: 'Nuova attività',
            }}
          />
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              onClick: () => {
                navigate(`/workactivities/expenses/new`)
              },
              hint: 'Inserisci spesa',
              text: 'Inserisci spesa',
              icon: 'fas fa-receipt',
              stylingMode: 'text',
            }}
          />
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Duplica',
              text: 'Duplica',
              icon: 'copy',
              onClick: () => {
                handleDuplicateActivityClick()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Elimina attività',
              text: 'Elimina attività',
              icon: 'trash',
              onClick: () => {
                onDeleteClick()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Inserisci giornate di ferie / malattia / infortunio / altro...',
              text: 'Inserisci giornate di ferie / malattia / infortunio / altro...',
              icon: 'fas fa-building',
              onClick: () => {
                popupInsertHolidaysSelectorRef.current?.instance().show()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.Administrator])}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Esporta rimborsi',
              text: 'Esporta rimborsi',
              icon: 'money',
              onClick: handleCurrentMonthtRiprefundClick,
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.Administrator])}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Esporta totale mese corrente',
              text: 'Esporta totale mese corrente',
              icon: 'formula',
              onClick: handleCurrentMonthSummaryClick,
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Esporta check utente mese',
              text: 'Esporta check utente mese',
              icon: 'check',
              onClick: handleCurrentMonthUserCheckClick,
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.Administrator])}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Invio report buoni pasto',
              text: 'Invio report buoni pasto',
              icon: 'food',
              onClick: (e: ClickEvent) => {
                window.open(`hours/mealvoucher`, '_blank')
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.Administrator])}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Anomalie Fluida',
              text: 'Anomalie Fluida',
              icon: 'warning',
              onClick: (e: ClickEvent) => {
                window.open(`hours/anomalies`, '_blank')
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            name="columnChooserButton"
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            options={{ hint: 'Scelta colonne', text: 'Scelta colonne' }}
          />
          <Item
            name="exportButton"
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            options={{ hint: 'Esporta dati in excel', text: 'Esporta dati in excel' }}
          />
          <Item name="searchPanel" />
        </Toolbar>
      </DataGrid>
      <h6
        style={{ fontSize: '0.9em', float: 'left', marginTop: '10px' }}
      >{`Totale [HH.MM] ${totals?.totalTime.hours}.${totals?.totalTime.minutes} - Straordinari ${totals?.extraTime} - Trasferta ${totals?.transferTime} - Buoni pasto ${totals?.totalMealVoucher}`}</h6>
      <Popup
        width={currentScreenSize.isLarge || currentScreenSize.isMedium ? 300 : currentScreenSize.isXSmall ? 350 : 700}
        height={'auto'}
        hideOnOutsideClick={true}
        showCloseButton={true}
        deferRendering={true}
        title={'Seleziona mese di riferimento'}
        ref={popupCurrentMonthUserCheckRef}
        className="monthusercheck-popup-content"
        resizeEnabled={false}
      >
        <MonthUserCheckSelector data={currentMonthUserCheckData} />
      </Popup>
      <InsertHolidaysSelector popupRef={popupInsertHolidaysSelectorRef} gridRef={gridRef}></InsertHolidaysSelector>
    </>
  )
}

export default HoursPage
